import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiClient from '../utils/apiClient';
import config from '../utils/config';

// ── Types matching w-hms responses ──

export interface ParameterReference {
  ID: number;
  CreatedAt: string;
  UpdatedAt: string;
  metric_code: string;
  metric_name: string;
  category: string;
  weight_category: string;       // vital | important | standard
  unit: string;
  normal_min: number;
  normal_max: number;
  critical_min: number;
  critical_max: number;
  description: string;
  gender: string;
  age_group_min: number;
  age_group_max: number;
  is_active: boolean;
  sort_order: number;
}

export interface CategoryScore {
  ID: number;
  health_score_id: number;
  category_name: string;
  weight_category: string;
  score: number;
  weight: number;
  parameter_count: number;
}

export interface ParameterScore {
  ID: number;
  health_score_id: number;
  metric_code: string;
  metric_name: string;
  value: number;
  unit: string;
  normal_min: number;
  normal_max: number;
  score: number;
  deviation_pct: number;
  status: string;
  weight_category: string;
  trend_direction: string;
  trend_factor: number;
}

export interface HealthScore {
  ID: number;
  CreatedAt: string;
  UpdatedAt: string;
  customer_id: number;
  overall_score: number;
  risk_level: string;            // low | moderate | high | critical
  trend_direction: string;
  total_weight: number;
  weighted_sum: number;
  parameter_count: number;
  summary: string;
  calculated_at: string;
  report_ids: string;
  categories?: CategoryScore[];
  parameters?: ParameterScore[];
}

export interface HealthTrend {
  ID: number;
  customer_id: number;
  overall_score: number;
  risk_level: string;
  trend_direction: string;
  parameter_count: number;
  recorded_at: string;
}

export interface CalculateScoreInput {
  customer_id: number;
  report_ids?: number[];
  metrics: { metric_code: string; value: number; unit?: string; previous_value?: number }[];
}

interface HealthScoresState {
  scores: HealthScore[];
  selectedScore: HealthScore | null;
  trends: HealthTrend[];
  references: ParameterReference[];
  loading: boolean;
  actionLoading: boolean;
  error: string | null;
}

const initialState: HealthScoresState = {
  scores: [],
  selectedScore: null,
  trends: [],
  references: [],
  loading: false,
  actionLoading: false,
  error: null,
};

// ── Thunks ──

export const fetchHealthScoresByCustomer = createAsyncThunk(
  'healthScores/fetchByCustomer',
  async (customerId: number, thunkAPI) => {
    try {
      const response = await apiClient.get(
        `${config.services.hms}/api/v1/health-scores/customer/${customerId}`
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message || 'Failed to fetch health scores');
    }
  }
);

export const fetchHealthTrendsByCustomer = createAsyncThunk(
  'healthScores/fetchTrendsByCustomer',
  async (customerId: number, thunkAPI) => {
    try {
      const response = await apiClient.get(
        `${config.services.hms}/api/v1/health-trends/customer/${customerId}`
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message || 'Failed to fetch trends');
    }
  }
);

export const calculateHealthScore = createAsyncThunk(
  'healthScores/calculate',
  async (data: CalculateScoreInput, thunkAPI) => {
    try {
      const response = await apiClient.post(
        `${config.services.hms}/api/v1/health-scores/calculate`,
        data
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message || 'Failed to calculate health score');
    }
  }
);

export const deleteHealthScore = createAsyncThunk(
  'healthScores/delete',
  async (id: number, thunkAPI) => {
    try {
      await apiClient.delete(`${config.services.hms}/api/v1/health-scores/${id}`);
      return id;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message || 'Failed to delete health score');
    }
  }
);

export const fetchParameterReferences = createAsyncThunk(
  'healthScores/fetchReferences',
  async (_, thunkAPI) => {
    try {
      const response = await apiClient.get(
        `${config.services.hms}/api/v1/parameter-references`
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message || 'Failed to fetch parameter references');
    }
  }
);

export const createParameterReference = createAsyncThunk(
  'healthScores/createReference',
  async (data: Partial<ParameterReference>, thunkAPI) => {
    try {
      const response = await apiClient.post(
        `${config.services.hms}/api/v1/parameter-references`,
        data
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message || 'Failed to create parameter reference');
    }
  }
);

export const updateParameterReference = createAsyncThunk(
  'healthScores/updateReference',
  async ({ id, data }: { id: number; data: Partial<ParameterReference> }, thunkAPI) => {
    try {
      const response = await apiClient.put(
        `${config.services.hms}/api/v1/parameter-references/${id}`,
        data
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message || 'Failed to update parameter reference');
    }
  }
);

export const deleteParameterReference = createAsyncThunk(
  'healthScores/deleteReference',
  async (id: number, thunkAPI) => {
    try {
      await apiClient.delete(`${config.services.hms}/api/v1/parameter-references/${id}`);
      return id;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message || 'Failed to delete parameter reference');
    }
  }
);

const unwrap = (p: any) => (Array.isArray(p?.data) ? p.data : Array.isArray(p) ? p : []);
const unwrapOne = (p: any) => p?.data || p || null;

const healthScoresSlice = createSlice({
  name: 'healthScores',
  initialState,
  reducers: {
    clearSelectedScore: (state) => { state.selectedScore = null; },
    clearHealthScoresError: (state) => { state.error = null; },
    resetHealthScores: (state) => { state.scores = []; state.trends = []; state.selectedScore = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchHealthScoresByCustomer.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchHealthScoresByCustomer.fulfilled, (state, action) => {
        state.loading = false;
        const data = unwrapOne(action.payload);
        // endpoint may return a single latest score or list
        state.scores = Array.isArray(data) ? data : data ? [data] : [];
        state.selectedScore = Array.isArray(data) ? data[0] || null : data;
      })
      .addCase(fetchHealthScoresByCustomer.rejected, (state, action) => {
        state.loading = false; state.error = action.payload as string;
      })
      .addCase(fetchHealthTrendsByCustomer.fulfilled, (state, action) => {
        state.trends = unwrap(action.payload);
      })
      .addCase(calculateHealthScore.pending, (state) => { state.actionLoading = true; })
      .addCase(calculateHealthScore.fulfilled, (state, action) => {
        state.actionLoading = false;
        const score = unwrapOne(action.payload);
        if (score) {
          state.selectedScore = score;
          state.scores = [score, ...state.scores];
        }
      })
      .addCase(calculateHealthScore.rejected, (state, action) => {
        state.actionLoading = false; state.error = action.payload as string;
      })
      .addCase(deleteHealthScore.fulfilled, (state, action) => {
        state.scores = state.scores.filter(s => s.ID !== action.payload);
      })
      .addCase(fetchParameterReferences.fulfilled, (state, action) => {
        state.references = unwrap(action.payload);
      })
      .addCase(createParameterReference.fulfilled, (state, action) => {
        const ref = unwrapOne(action.payload);
        if (ref) state.references.push(ref);
      })
      .addCase(updateParameterReference.fulfilled, (state, action) => {
        const ref = unwrapOne(action.payload);
        if (ref) {
          const idx = state.references.findIndex(r => r.ID === ref.ID);
          if (idx !== -1) state.references[idx] = ref;
        }
      })
      .addCase(deleteParameterReference.fulfilled, (state, action) => {
        state.references = state.references.filter(r => r.ID !== action.payload);
      });
  },
});

export const { clearSelectedScore, clearHealthScoresError, resetHealthScores } = healthScoresSlice.actions;
export default healthScoresSlice.reducer;
